import {
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { SourceKnowledgeCitation } from './sourceKnowledgeService';

export type SourceAskMessageRole = 'user' | 'assistant';

export interface SourceAskConversationMessage {
  role: SourceAskMessageRole;
  text: string;
  citations?: SourceKnowledgeCitation[];
  groundingStatus?: string;
  createdAt?: any;
}

function getSessionRefs(sourceSetId: string, userId: string) {
  const sessionId = userId;
  const sessionRef = doc(db, 'sourceSets', sourceSetId, 'askSessions', sessionId);
  const messagesRef = collection(sessionRef, 'messages');

  return { sessionId, sessionRef, messagesRef };
}

function cleanCitations(citations: SourceKnowledgeCitation[] = []): SourceKnowledgeCitation[] {
  return citations.map((citation) => ({
    citationId: String(citation.citationId || ''),
    sourceId: String(citation.sourceId || ''),
    sourceName: String(citation.sourceName || ''),
    sourceType: String(citation.sourceType || ''),
    url: citation.url || '',
    excerpt: String(citation.excerpt || ''),
    location: citation.location || null
  }));
}

export async function loadAskSourceConversation(
  sourceSetId: string,
  userId: string,
  maxMessages = 80
): Promise<SourceAskConversationMessage[]> {
  if (!sourceSetId || !userId) return [];

  try {
    const { messagesRef } = getSessionRefs(sourceSetId, userId);
    const messagesQuery = query(messagesRef, orderBy('createdAt', 'desc'), limit(maxMessages));
    const snapshot = await getDocs(messagesQuery);

    return snapshot.docs
      .map((messageDoc) => {
        const data = messageDoc.data();

        return {
          role: data.role === 'assistant' ? 'assistant' : 'user',
          text: String(data.text || ''),
          citations: Array.isArray(data.citations) ? data.citations : [],
          groundingStatus: data.groundingStatus || undefined,
          createdAt: data.createdAt || null
        } as SourceAskConversationMessage;
      })
      .reverse()
      .filter((message) => message.text.trim().length > 0);
  } catch (error) {
    console.warn('[AskSourceHistory] Failed to load conversation:', error);
    return [];
  }
}

export async function saveAskSourceTurn(args: {
  sourceSetId: string;
  userId: string;
  question: string;
  answer: string;
  citations?: SourceKnowledgeCitation[];
  groundingStatus?: string;
}) {
  const { sourceSetId, userId, question, answer, citations = [], groundingStatus } = args;

  if (!sourceSetId || !userId || !question.trim() || !answer.trim()) return;

  const { sessionRef, messagesRef } = getSessionRefs(sourceSetId, userId);
  const batch = writeBatch(db);
  const userMessageRef = doc(messagesRef);
  const assistantMessageRef = doc(messagesRef);

  batch.set(sessionRef, {
    sessionId: userId,
    sourceSetId,
    userId,
    updatedAt: serverTimestamp(),
    lastMessageAt: serverTimestamp()
  }, { merge: true });

  batch.set(userMessageRef, {
    sourceSetId,
    userId,
    role: 'user',
    text: question,
    citations: [],
    createdAt: serverTimestamp()
  });

  batch.set(assistantMessageRef, {
    sourceSetId,
    userId,
    role: 'assistant',
    text: answer,
    citations: cleanCitations(citations),
    groundingStatus: groundingStatus || 'partially_grounded',
    createdAt: serverTimestamp()
  });

  await batch.commit();
}

export async function clearAskSourceConversation(sourceSetId: string, userId: string) {
  if (!sourceSetId || !userId) return;

  const { sessionRef, messagesRef } = getSessionRefs(sourceSetId, userId);
  const messagesQuery = query(messagesRef, orderBy('createdAt', 'desc'), limit(100));
  const snapshot = await getDocs(messagesQuery);
  const batch = writeBatch(db);

  snapshot.docs.forEach((messageDoc) => {
    batch.delete(messageDoc.ref);
  });

  batch.set(sessionRef, {
    sessionId: userId,
    sourceSetId,
    userId,
    updatedAt: serverTimestamp(),
    lastClearedAt: serverTimestamp()
  }, { merge: true });

  await batch.commit();
}
